/** @format */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { DegreeProgrammeAttainment, DPSMBase, ModuleAttainment, OtmId, StudyModule } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { AppErrorHandler, CurriculumPeriodNameService, SelectOption } from 'sis-components';

@Component({
    selector: 'app-student-module-info-modal-header',
    templateUrl: './student-module-info-modal-header.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StudentModuleInfoModalHeaderComponent implements OnInit, OnDestroy {
    private readonly ref = inject(ChangeDetectorRef);
    private readonly curriculumPeriodNameService = inject(CurriculumPeriodNameService);
    private readonly appErrorHandler = inject(AppErrorHandler);

    @Input() attainment: ModuleAttainment | DegreeProgrammeAttainment;
    @Input() module: DPSMBase;
    @Input() versions: DPSMBase[];
    /** Whether the currently viewed version is compatible with the plan and can be taken into use. */
    @Input() canUseCurrentVersion = false;
    /** Whether the currently viewed version differs from the version selected in the plan. */
    @Input() versionChanged = false;

    @Output() setModuleInfoVersionChange = new EventEmitter<DPSMBase>();
    /** Emitted when the student chooses to take the currently viewed version into use. */
    @Output() saveVersionChange = new EventEmitter<void>();

    options: SelectOption[] = [];
    versionSelectOptionsSubscription: Subscription = null;
    popoverLabelLocalizationKey: string;
    popoverInfoLocalizationKey: string;
    moduleLocalizationKey: string;
    versionCompatibleLocalizationKey: string;
    versionNotCompatibleLocalizationKey: string;
    readonly switchVersionLocalizationKey = 'MODULE_INFO.VERSION_USE_BTN';

    ngOnInit() {
        switch (this.module.type as 'DegreeProgramme' | 'StudyModule') {
            case 'DegreeProgramme': {
                this.moduleLocalizationKey = 'ENTITY_NAMES.DegreeProgramme';
                this.popoverLabelLocalizationKey = 'MODULE_INFO.DEGREE_PROGRAMME_VERSION';
                this.popoverInfoLocalizationKey = 'MODULE_INFO.DEGREE_PROGRAMME_VERSION_POPOVER_INFO';
                this.versionCompatibleLocalizationKey = 'MODULE_INFO.DEGREE_PROGRAMME_VERSION_IS_COMPATIBLE';
                this.versionNotCompatibleLocalizationKey = 'MODULE_INFO.DEGREE_PROGRAMME_VERSION_NOT_COMPATIBLE';
                break;
            }
            case 'StudyModule': {
                this.moduleLocalizationKey = 'ENTITY_NAMES.StudyModule';
                this.popoverLabelLocalizationKey = 'MODULE_INFO.STUDY_MODULE_VERSION';
                this.popoverInfoLocalizationKey = 'MODULE_INFO.STUDY_MODULE_VERSION_POPOVER_INFO';
                this.versionCompatibleLocalizationKey = 'MODULE_INFO.STUDY_MODULE_VERSION_IS_COMPATIBLE';
                this.versionNotCompatibleLocalizationKey = 'MODULE_INFO.STUDY_MODULE_VERSION_NOT_COMPATIBLE';
                break;
            }
            default: {
                break;
            }
        }

        // getVersionSelectOptions is typed for StudyModule[] but only reads Module fields (id, curriculumPeriodIds);
        // versions may also be DegreeProgramme, so assert the narrower type at this single boundary.
        this.versionSelectOptionsSubscription = this.curriculumPeriodNameService
            .getVersionSelectOptions(this.versions as StudyModule[])
            .pipe(this.appErrorHandler.defaultErrorHandler())
            .subscribe(result => {
                this.options = result;
                this.ref.markForCheck();
            });
    }

    ngOnDestroy() {
        this.versionSelectOptionsSubscription?.unsubscribe();
    }

    changeVersion(versionId: OtmId): void {
        const selectedVersion = _.find(this.versions, ver => ver.id === versionId);
        this.setModuleInfoVersionChange.emit(selectedVersion);
    }
}
